/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from 'react'
import { PlaceShort, RemoteFieldMetadata, TripShort } from 'types/entity'

interface _Settings {
  featureFlags: {
    // ENABLE_PAY_PERIOD_AI: boolean
    ENABLE_EVERLANCE_PAYMENTS: boolean
    // ENABLE_CHANGELOG: boolean
    ENABLE_ONBOARDING_M3: boolean
    ENABLE_NEXT_PROGRAM: boolean
    ENABLE_PAYMENTS_M3: boolean
    ENABLE_OUTLIERS_2: boolean
    ENABLE_B2C_2: boolean
    ENABLE_TAAP2: boolean
    ENABLE_OLD_LOOMS: boolean
    ENABLE_CATALOG: boolean
    ENABLE_DRIVER_EXPERIENCE: boolean
    ENABLE_GREEN_APP: boolean
  }
  tripDetail: {
    tokenId: string | 'new-trip-id'
    view: 'personal' | 'team'
    props?: Record<string, unknown>
  }
  pollingJobs: {
    memberBulkInviteJobId: string
    memberBatchInviteJobId: string
    memberBulkUpdateJobId: string
    memberExportJobId: string
    payPeriodActionJobId: string
    payPeriodDownloadJobId: string
    reportBulkUpdateJobId: string
    fundingRequestJobId: string
    downloadSummaryJobId: string
    importTeamPlacesJobId: string
    mergeGroupIntegrationId: string
    downloadMergeManagersJobId: string
    memberBatchDeleteInviteJobId: string
    mergePreviewEmployeedJobId: string
    resetDemoAccountJobId: string
    reportsActionJobId: string
    taxReportJobId: string
  }
  transactionDetail: {
    tokenId: string | 'new-id-expense' | 'new-id-revenue'
    view: 'personal' | 'team'
    props?: Record<string, unknown>
  }
  inDrawerReport: {
    view: 'report' | 'trip' | 'expense' | 'rules' | 'pay'
    activeId: string
  }
  memberInDrawerView: {
    view:
      | 'main'
      | 'trip'
      | 'expense'
      | 'report'
      | 'report-trip'
      | 'report-expense'
      | 'report-rules'
    activeId: string
    prevReportId: string | null
  }
  mfa: {
    action: 'authenticate' | null
    accountId: string | undefined
    customerToken: string | undefined
    onSuccess: (() => void) | null
    onClose: (() => void) | null
    onBack: (() => void) | null
    continueButtonText: string
    language: string
    themes: string[]
  }
  addToReportView: {
    view: 'main' | 'trip' | 'expense' | 'new-trip' | 'new-expense'
    activeId: string
  }
  showWelcomeModal: boolean
  inAppView: boolean
  reimbursementTabParams: {
    pay_periods: string
    reports: string
    fixed: string
    variable: string
  }
  tooltipTutorial: {
    sideNavStep: 'account-switcher' | 'compliance' | 'collapse' | null
  }
  selectedRemoteFields: RemoteFieldMetadata[]
  ratingModal: {
    active: boolean
    trigger: string
  }
  fundingModal: {
    show: boolean
    showSuccessModal: boolean
  }
  auth: {
    isAuthenticated: boolean
    isAuthLoading: boolean
    userTokenID: string | null
    accessToken: string | null
    refreshToken: string | null
  }
  classifyLocationModal: {
    tripId: string
    place: TripShort['end_place'] | null
    classification: PlaceShort['classification'] | null
  }
}

export type Settings = Readonly<_Settings>

interface SettingsContextType {
  data: Settings
  /**
   * @description: Update the global settings context.
   */
  update: (newValue: Settings) => void
  /**
   * @description: Get value from key
   */
  get: (key: keyof Settings) => any
  /**
   * @description: Set value from key
   */

  set: (key: keyof Settings, newValue: any) => void
  /**
   * @description: Reset the global settings context to the default value, provided at src/index.tsx
   */
  reset: VoidFunction
}

const SettingsContext = createContext({} as SettingsContextType)

/**
 * @description: Settings is a replacement for Redux concerning app-wide states that need to be accessed globally. For server-side data, use react-query. For local data or form data, use react-hook-form.
 *  To use context, import useSettings hook.
 */
export function SettingsProvider({
  children,
  value
}: {
  children: ReactNode | ReactNode[]
  value: Settings
}) {
  const [state, setState] = useState(value)
  const reset = useCallback(() => setState(value), [setState, value])

  const get = useCallback(
    (key: keyof Settings) => {
      return state[key]
    },
    [state]
  )
  const set = useCallback((key: keyof Settings, value: any) => {
    setState((prevState) => ({ ...prevState, [key]: value }))
  }, [])

  return (
    <SettingsContext.Provider
      value={{ data: state, update: setState, reset, get, set }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

/**
 * @description: Settings is a replacement for Redux concerning app-wide states that need to be accessed globally. For server-side data, use react-query. For local data or form data, use react-hook-form.
 * @returns: { data, update, reset, get, set } where data is the snapshot of the settings, and update is a function that replaces the current with a new, and reset undo all changes to the default value supplied to the provider, get accepts a key that can directly return the information from the main object, set can update the information specifying the key from the settings object.
 */
export const useSettings = (): SettingsContextType =>
  useContext(SettingsContext)
